import { useState } from "react";
import { Plus, Pencil, Power, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../../lib/api";
import { PageHeader, Badge, SkeletonGrid, ErrorState, EmptyState, initials } from "../../components/common";
import { Field, Input, Select, STATUS_OPTS, Toolbar, Table, Row, Td, IconBtn, FormDialog } from "../../components/admin/kit";

const EMPTY = { first_name: "", last_name: "", tc: "", phone: "", email: "", password: "", status: "active" };

export default function AdminStudents() {
  const { data, loading, error, reload } = useApi("/admin/students");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      Object.keys(body).forEach((k) => body[k] === "" && delete body[k]);
      delete body.id;
      if (form.id) await api.put(`/admin/students/${form.id}`, body);
      else await api.post("/admin/students", body);
      toast.success(form.id ? "Kursiyer güncellendi." : "Kursiyer oluşturuldu.");
      setForm(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const toggle = async (s) => {
    try {
      await api.put(`/admin/students/${s.id}`, { status: s.status === "active" ? "passive" : "active" });
      toast.success("Durum güncellendi.");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const list = (data || []).filter((s) => `${s.name} ${s.email} ${s.phone}`.toLocaleLowerCase("tr-TR").includes(q.toLocaleLowerCase("tr-TR")));

  return (
    <div>
      <PageHeader eyebrow="Yönetim" title="Kursiyerler" desc="Kursiyer hesaplarını oluşturun ve yönetin." />
      <Toolbar q={q} setQ={setQ} placeholder="Ad, e-posta veya telefon ara..." action={
        <button onClick={() => setForm(EMPTY)} className="btn-primary inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-semibold" data-testid="add-student-btn"><Plus className="h-4 w-4" /> Yeni Kursiyer</button>
      } />
      {error ? <ErrorState message={error} onRetry={reload} /> : loading ? <SkeletonGrid count={1} className="h-80" cols="" /> : list.length === 0 ? (
        <EmptyState icon={Users} title="Kursiyer bulunamadı." desc="Yeni bir kursiyer ekleyerek başlayın." />
      ) : (
        <Table head={["Kursiyer", "Telefon", "T.C.", "Eğitim", "Durum", ""]} testid="students-table">
          {list.map((s) => (
            <Row key={s.id} data-testid={`student-row-${s.id}`}>
              <Td>
                <div className="flex items-center gap-3">
                  <div className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-xs font-bold">{initials(s.name)}</div>
                  <div><p className="font-semibold text-[var(--ink)]">{s.name}</p><p className="text-xs text-slate-400">{s.email || "—"}</p></div>
                </div>
              </Td>
              <Td className="text-slate-600">{s.phone || "—"}</Td>
              <Td className="font-mono text-xs text-slate-600">{s.tc_masked || "—"}</Td>
              <Td>{s.assignment_count}</Td>
              <Td><Badge status={s.status} testid={`student-status-${s.id}`} /></Td>
              <Td>
                <div className="flex justify-end gap-1">
                  <IconBtn icon={Pencil} label="Düzenle" onClick={() => setForm({ ...EMPTY, ...s, tc: "", password: "" })} data-testid={`edit-student-${s.id}`} />
                  <IconBtn icon={Power} label={s.status === "active" ? "Pasif yap" : "Aktif yap"} onClick={() => toggle(s)} data-testid={`toggle-student-${s.id}`} />
                </div>
              </Td>
            </Row>
          ))}
        </Table>
      )}
      <FormDialog open={!!form} onClose={() => setForm(null)} title={form?.id ? "Kursiyeri Düzenle" : "Yeni Kursiyer"} desc="Kursiyer e-posta, telefon veya T.C. ile giriş yapabilir." onSubmit={save} saving={saving} testid="student-form-dialog">
        {form && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Ad"><Input value={form.first_name} onChange={set("first_name")} data-testid="student-first-name-input" required /></Field>
              <Field label="Soyad"><Input value={form.last_name} onChange={set("last_name")} data-testid="student-last-name-input" required /></Field>
            </div>
            <Field label={form.id ? `T.C. Kimlik No (mevcut: ${form.tc_masked || "—"})` : "T.C. Kimlik No"}><Input value={form.tc} onChange={set("tc")} maxLength={11} inputMode="numeric" placeholder={form.id ? "Değiştirmek için yeni T.C. girin" : "11 haneli"} data-testid="student-tc-input" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Telefon"><Input value={form.phone} onChange={set("phone")} placeholder="05XX XXX XX XX" data-testid="student-phone-input" /></Field>
              <Field label="E-posta"><Input type="email" value={form.email} onChange={set("email")} data-testid="student-email-input" /></Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={form.id ? "Yeni Şifre (opsiyonel)" : "Şifre"}><Input type="text" value={form.password} onChange={set("password")} placeholder="En az 8 karakter" data-testid="student-password-input" /></Field>
              <Field label="Durum"><Select value={form.status} onChange={set("status")} options={STATUS_OPTS} data-testid="student-status-select" /></Field>
            </div>
          </>
        )}
      </FormDialog>
    </div>
  );
}
